/**
 * learn/boss-gallery.ts — Boss Gallery UI
 *
 * Lists all 9 bosses (Tier 1/2/3).
 * - Defeated bosses: coloured portrait + HP + quote + best score.
 * - Undefeated bosses: grey silhouette + "??? 尚未邂逅".
 * Clicking a boss opens a detail modal with attack pattern description.
 */

import { loadProgress } from '../shared/progress';
import type { PlayerProgress } from '../shared/types';

// ── Static boss catalogue ─────────────────────────────────────────────────────

interface BossEntry {
  id: string;
  displayName: string;
  tier: 1 | 2 | 3;
  faction: string;
  factionEmoji: string;
  hp: number;
  color: string;       // CSS hex, e.g. '#6a1b9a'
  quote: string;       // boss flavour line shown when defeated
  attackDesc: string;  // attack pattern description for detail modal
}

const ALL_BOSSES: BossEntry[] = [
  // ── Tier 1 ──────────────────────────────────────────────────────────────────
  {
    id: 'shu-mo',
    displayName: '數魔・乘九',
    tier: 1,
    faction: 'celestial',
    factionEmoji: '⚡',
    hp: 200,
    color: '#6a1b9a',
    quote: '算術是宇宙的語言，你能破解嗎？',
    attackDesc:
      '【攻擊A】精準單發：朝玩家方向射出高速子彈（350px/s，15傷害）。' +
      '【攻擊B・Phase 2】召喚增援：生成 3 隻小怪側翼夾擊。',
  },
  {
    id: 'fu-hun',
    displayName: '符魂・舌劍',
    tier: 1,
    faction: 'runic',
    factionEmoji: '🔮',
    hp: 250,
    color: '#ad1457',
    quote: '文字是枷鎖，也是解放的鑰匙。',
    attackDesc:
      '【攻擊A】雙向符刃：左右各一發扇形子彈（300px/s，12傷害）。' +
      '【攻擊B・Phase 2】符文沉默：短暫封印玩家技能 0.5 秒。',
  },
  {
    id: 'yuan-su',
    displayName: '元素守衛',
    tier: 1,
    faction: 'elemental',
    factionEmoji: '🌀',
    hp: 180,
    color: '#00897b',
    quote: '水、火、土、風——自然從不妥協。',
    attackDesc:
      '【攻擊A】冰封突刺：凍結玩家 0.25 秒，隨後射出直線子彈（280px/s，10傷害）。' +
      '【攻擊B・Phase 2】五芒扇射：五方向同時射出子彈（250px/s，8傷害）。',
  },
  // ── Tier 2 ──────────────────────────────────────────────────────────────────
  {
    id: 'shu-fu',
    displayName: '數符雙煞',
    tier: 2,
    faction: 'celestial',
    factionEmoji: '⚡',
    hp: 400,
    color: '#7c1fa1',
    quote: '數與符，缺一不可。聯手才能征服一切！',
    attackDesc:
      '【攻擊A】交替：奇數輪高速子彈（380px/s，20傷害）；偶數輪範圍沉默波（200px，0.6秒）。' +
      '【攻擊B・Phase 2】召喚 2 小怪，持續補充火力。',
  },
  {
    id: 'ji-yuan',
    displayName: '機元雙影',
    tier: 2,
    faction: 'techno',
    factionEmoji: '🤖',
    hp: 380,
    color: '#00acc1',
    quote: '機械與自然，速度與力量，完美融合！',
    attackDesc:
      '【攻擊A】無人機群：召喚 4 隻小怪（速度極快）。' +
      '【攻擊B・Phase 2】冰刺扇射：五角扇形冷凍彈，凍結全區域。',
  },
  {
    id: 'xing-yu',
    displayName: '星域守衛',
    tier: 2,
    faction: 'celestial',
    factionEmoji: '⚡',
    hp: 350,
    color: '#3949ab',
    quote: '星辰之力，你承受得住嗎？',
    attackDesc:
      '【攻擊A】追蹤子彈：發射 2 枚跟蹤彈（260px/s，18傷害），持續調整角度追擊。' +
      '【攻擊B・Phase 2】星爆：以自身為圓心 8 方向放射子彈（300px/s）。',
  },
  {
    id: 'zi-fu',
    displayName: '自符魂使',
    tier: 2,
    faction: 'runic',
    factionEmoji: '🔮',
    hp: 420,
    color: '#c62828',
    quote: '自然之書，字字見血！',
    attackDesc:
      '【攻擊A】詛咒波：發射慢速但高傷害符文彈（150px/s，30傷害）。' +
      '【攻擊B・Phase 2】沉默風暴：連續 3 次沉默（各 0.4 秒），間隔 0.8 秒。',
  },
  // ── Tier 3 ──────────────────────────────────────────────────────────────────
  {
    id: 'shi-zhong',
    displayName: '期中總魔・三相之劫',
    tier: 3,
    faction: 'celestial',
    factionEmoji: '⚡',
    hp: 800,
    color: '#bf360c',
    quote: '三相合一，天地震顫——你敢應戰嗎？',
    attackDesc:
      '【Phase 1】多方向扇射（5角，400px/s，25傷害）+ 召喚小怪（3隻）。' +
      '【Phase 2 ≤400HP】追加沉默波（範圍350px）+ 高速追蹤彈。' +
      '【Phase 3 ≤200HP】全螢幕覆蓋子彈風暴（8方向，450px/s）。',
  },
  {
    id: 'tian-ming',
    displayName: '期末王・天命召喚者',
    tier: 3,
    faction: 'dominion',
    factionEmoji: '🏰',
    hp: 1200,
    color: '#1a237e',
    quote: '天命已定！唯有征服者方能主宰永恆要塞！',
    attackDesc:
      '【Phase 1】連續召喚（最多 8 隻小怪）+ 慢速覆蓋彈（6方向，200px/s）。' +
      '【Phase 2 ≤600HP】冰封+沉默聯鎖：每 4 秒一次，持續 1 秒。' +
      '【Phase 3 ≤300HP】天命審判：全螢幕閃爍 + 12 方向追蹤彈陣（480px/s，40傷害）。',
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function tierLabel(tier: 1 | 2 | 3): string {
  return tier === 1 ? 'Tier 1' : tier === 2 ? 'Tier 2' : 'Tier 3';
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function bestScoreFor(progress: PlayerProgress | null, bossId: string): string {
  if (!progress) return '—';
  // Try towerHistory for floor/score data
  const runs = progress.towerHistory ?? [];
  if (runs.length > 0) {
    const best = Math.max(...runs.map((r) => r.score));
    if (best > 0) return `最高分 ${best}`;
  }
  // Fallback: just show "已打敗"
  return '已打敗';
  void bossId;
}

// ── Render ────────────────────────────────────────────────────────────────────

export function renderBossGallery(
  container: HTMLElement,
  onBack: () => void,
): void {
  const progress = loadProgress();
  const defeated = new Set(progress?.bossesDefeated ?? []);

  const root = document.createElement('div');
  root.className = 'boss-gallery';
  container.appendChild(root);

  // 戰利品廳樣式(boss 2026-06-17 P1):打敗的魔王 = 戰利品。自包含 <style>,注入一次。
  const STYLE_ID = 'bg-trophy-style';
  if (!document.getElementById(STYLE_ID)) {
    const st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent =
      '.bg-card--defeated{position:relative;box-shadow:0 0 0 1px rgba(255,213,79,.45),0 4px 16px rgba(255,213,79,.14)}' +
      '.bg-portrait{position:relative}' +
      '.bg-trophy-badge{position:absolute;top:5px;right:5px;font-size:10px;font-weight:800;color:#1a2730;' +
      'background:linear-gradient(135deg,#ffd54f,#ffb300);padding:2px 7px;border-radius:99px;' +
      'box-shadow:0 1px 4px rgba(0,0,0,.35);letter-spacing:.5px}' +
      '.bg-trophy-banner{background:linear-gradient(135deg,#2a3f5f,#1a2730);border:1px solid #3a536b;' +
      'border-left:4px solid #ffd54f;border-radius:12px;padding:12px 16px;margin:0 0 14px;' +
      'display:flex;align-items:center;gap:14px}' +
      '.bg-trophy-banner .n{font-size:30px;font-weight:900;color:#ffd54f;line-height:1}' +
      '.bg-trophy-banner .t{font-size:13px;color:#cfe0e6}';
    document.head.appendChild(st);
  }

  const header = document.createElement('div');
  header.className = 'boss-gallery-header';
  header.innerHTML = `
    <button class="secondary-btn" id="bg-back">← 返回</button>
    <h2>🏆 戰利品廳</h2>
    <p class="bg-subtitle">你征服過的魔王都在這裡展示</p>
  `;
  root.appendChild(header);

  // 戰績橫幅:征服數一眼可見(炫耀感)。
  const banner = document.createElement('div');
  banner.className = 'bg-trophy-banner';
  banner.innerHTML =
    `<span class="n">${defeated.size}<span style="font-size:16px;color:#8aa3ad">/${ALL_BOSSES.length}</span></span>` +
    `<span class="t">隻魔王已被你征服<br>打敗更多魔王,戰利品廳就越滿</span>`;
  root.appendChild(banner);
  root.querySelector('#bg-back')!.addEventListener('click', () => {
    root.remove();
    onBack();
  });

  // Group by tier
  const tiers: Array<1 | 2 | 3> = [1, 2, 3];
  for (const tier of tiers) {
    const bosses = ALL_BOSSES.filter((b) => b.tier === tier);

    const section = document.createElement('div');
    section.className = 'bg-tier-section';
    section.innerHTML = `<h3 class="bg-tier-heading">${tierLabel(tier)}</h3>`;
    root.appendChild(section);

    const grid = document.createElement('div');
    grid.className = 'bg-grid';
    section.appendChild(grid);

    for (const boss of bosses) {
      const isDefeated = defeated.has(boss.id);
      const card = document.createElement('div');
      card.className = `bg-card${isDefeated ? ' bg-card--defeated' : ' bg-card--locked'}`;
      card.dataset.bossId = boss.id;

      if (isDefeated) {
        card.innerHTML = `
          <div class="bg-portrait" style="background-color:${escapeHtml(boss.color)}20; border:2px solid ${escapeHtml(boss.color)};">
            <span class="bg-faction-emoji">${escapeHtml(boss.factionEmoji)}</span>
            <span class="bg-trophy-badge">🏆 已征服</span>
          </div>
          <div class="bg-info">
            <div class="bg-name">${escapeHtml(boss.displayName)}</div>
            <div class="bg-hp">HP ${boss.hp}</div>
            <div class="bg-score">${escapeHtml(bestScoreFor(progress, boss.id))}</div>
            <div class="bg-quote">"${escapeHtml(boss.quote)}"</div>
          </div>
        `;
      } else {
        card.innerHTML = `
          <div class="bg-portrait bg-portrait--locked">
            <span class="bg-faction-emoji">❓</span>
          </div>
          <div class="bg-info">
            <div class="bg-name bg-name--locked">??? 尚未邂逅</div>
          </div>
        `;
      }

      card.addEventListener('click', () => openDetailModal(root, boss, isDefeated));
      grid.appendChild(card);
    }
  }
}

// ── Detail modal ──────────────────────────────────────────────────────────────

function openDetailModal(
  galleryRoot: HTMLElement,
  boss: BossEntry,
  isDefeated: boolean,
): void {
  // Remove existing modal if any
  galleryRoot.querySelector('.bg-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'bg-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'bg-modal';

  if (isDefeated) {
    modal.innerHTML = `
      <div class="bg-modal-header" style="border-left:4px solid ${escapeHtml(boss.color)};">
        <span class="bg-modal-emoji">${escapeHtml(boss.factionEmoji)}</span>
        <h3>${escapeHtml(boss.displayName)}</h3>
        <span class="bg-modal-tier">${tierLabel(boss.tier)}</span>
      </div>
      <div class="bg-modal-hp">HP: ${boss.hp}</div>
      <div class="bg-modal-quote">"${escapeHtml(boss.quote)}"</div>
      <div class="bg-modal-attacks">
        <h4>攻擊模式</h4>
        <p>${escapeHtml(boss.attackDesc)}</p>
      </div>
      <button class="primary-btn bg-modal-close">關閉</button>
    `;
  } else {
    modal.innerHTML = `
      <div class="bg-modal-header">
        <span class="bg-modal-emoji">❓</span>
        <h3>??? 尚未邂逅</h3>
        <span class="bg-modal-tier">${tierLabel(boss.tier)}</span>
      </div>
      <p>打敗這隻 Boss 才能解鎖圖鑑資料。</p>
      <button class="primary-btn bg-modal-close">關閉</button>
    `;
  }

  overlay.appendChild(modal);
  galleryRoot.appendChild(overlay);

  const close = () => overlay.remove();
  modal.querySelector('.bg-modal-close')!.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
}
